import React from "react";
import { Table } from "react-bootstrap";
import ToggleBar from "../../commoncomponents/toggleBar";
import { utcToReportDate } from "../../../utils/dateConvert";
import { downloadCSVFile } from '../../../api/apiClient';
import Location from "../../commoncomponents/location";
import ShowIButtonName from "../../commoncomponents/showIButtonName.js";
import { getFriendlyHeading } from "../../../utils/math";
import { formatTemperatureReading, getValidTemperature, temperatureReadingClass } from "../../../utils/temperature";

const getNumberValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const formatOneDecimal = (value) => {
  const numberValue = getNumberValue(value);
  return numberValue === null ? "-" : numberValue.toFixed(1);
};

const formatHeading = (value) => {
  const numberValue = getNumberValue(value);
  return numberValue === null ? "-" : getFriendlyHeading(numberValue) || "-";
};

const getLogDescription = (element) => (
  element.logIdShortDescription ??
  element.LogIdShortDescription ??
  element.logIDShortDescription ??
  ""
);

const getVoltageValue = (element) => (
  element.deviceVolts ??
  element.devicevolts ??
  element.DeviceVolts ??
  element.volts ??
  element.volt ??
  element.Volts ??
  element.Volt
);

const formatVolts = (value) => {
  const numberValue = getNumberValue(value);
  return numberValue === null ? "" : `${numberValue.toFixed(1)}V`;
};

const isLowVoltage = (value) => {
  const numberValue = getNumberValue(value);
  return numberValue !== null && numberValue < 12;
};

const renderVoltage = (value) => {
  const formattedVolts = formatVolts(value);
  if (!formattedVolts) {
    return "-";
  }

  const lowVoltage = isLowVoltage(value);
  return (
    <span className={lowVoltage ? "locationVoltageValue low-voltage" : "locationVoltageValue"}>
      {lowVoltage && <span className="voltage-warning-icon" title="Low voltage">⚠️</span>}
      {formattedVolts}
    </span>
  );
};

const renderIgnition = (value) => {
  const isDriving = value == 1 || String(value).toLowerCase() === "true" || String(value).toLowerCase() === "driving";
  return (
    <span className={isDriving ? "engine-driving" : "engine-park"} title={isDriving ? "Drive" : "Parked"}>
      {isDriving ? "D" : "P"}
    </span>
  );
};

const formatIgnition = (value) => (
  value == 1 || String(value).toLowerCase() === "true" || String(value).toLowerCase() === "driving"
    ? "Drive"
    : "Parked"
);

const getIo8Value = (element) => (
  element.iO8 ??
  element.IO8 ??
  element.io8 ??
  element.Io8 ??
  ""
);

const renderFridge = (value) => {
  const isOn = value == 1 || String(value).toLowerCase() === "true" || String(value).toLowerCase() === "on";
  return (
    <span className={isOn ? "fridge-pill fridge-on" : "fridge-pill fridge-off"}>
      <i className="fa fa-snowflake-o fridge-snowflake" aria-hidden="true"></i>
      {isOn ? "ON" : "OFF"}
    </span>
  );
};

const renderDoorSensor = (value) => {
  const isClosed = String(value).toLowerCase() === "closed" || value == 1 || String(value).toLowerCase() === "true";
  return (
    <span className={isClosed ? "door-sensor-pill door-sensor-closed" : "door-sensor-pill door-sensor-open"}>
      {isClosed ? "Closed" : "Open"}
    </span>
  );
};

const renderGps = (value) => {
  const hasGps = String(value).toUpperCase() === "A" || value === true || value == 1;
  return (
    <span className={hasGps ? "locationGpsBadge gps-valid" : "locationGpsBadge gps-invalid"} title={hasGps ? "GPS valid" : "GPS invalid"}>
      <i className="fa fa-globe" aria-hidden="true"></i>
    </span>
  );
};

function LocationTable(props) {
  let tempAEnabled = props.tempAEnabled;
  let tempBEnabled = props.tempBEnabled;
  let driverIDEnabled = props.driverIDEnabled;
  let doorSensorEnabled = props.doorSensorEnabled;
  const showVoltage = String(props.deviceType || "").trim().toUpperCase() !== "MD101";
  let assetId = props.assetId;
  let assetCustomerName = props.assetCustomerName;
  const locationData = props.locationData || [];
  let recordCount = locationData.length;
  let timeZone = props.timeZone ? props.timeZone.toString().slice(25, 55) : '';
  const driverLoginEnabled = locationData.some((element) => {
    const driverName = element.driverName || element.DriverName;
    return driverName && driverName.toString().trim();
  });
  const data = (
    <Table responsive striped>
      <thead>
        <tr className="nowwrapTH">
          <th>ID</th>
          <th>Created</th>
          <th>Location</th>

          <th>LogID</th>
          <th>IO8</th>
          <th>Ignition</th>
          <th>Speed</th>
          <th>Heading</th>
          <th>Trip</th>
          <th>Odometer</th>
          <th>Fridge</th>
          {tempAEnabled && <th>TempA</th>}
          {tempBEnabled && <th>TempB</th>}
          {showVoltage && <th>Volts</th>}
          <th>Sat</th>
          <th>GPS</th>

          <th>GSM</th>
          {driverIDEnabled && <th>Driver ID</th>}
          {driverLoginEnabled && <th>Driver</th>}
          {doorSensorEnabled && <th>Door</th>}
        </tr>
      </thead>
      <tbody>
        {locationData.map((element) => {
          return (
            <tr>
              <td>{element.id}</td>
              <td>{utcToReportDate(element.created)}</td>
              <td>
                <Location
                  address={element.location}
                  lat={element.latitude}
                  lng={element.longitude}
                />
              </td>

              <td title={getLogDescription(element)}>{element.logID}</td>
              <td>{getIo8Value(element)}</td>
              <td>{renderIgnition(element.ignition)}</td>
              <td>{formatOneDecimal(element.speed)}</td>
              <td>{formatHeading(element.heading)}</td>
              <td>{formatOneDecimal(element.trip)}</td>
              <td>{formatOneDecimal(element.odometer)}</td>
              <td>{renderFridge(element.fridge)}</td>
              {tempAEnabled && <td className={temperatureReadingClass(element.tempA)}>{formatTemperatureReading(element.tempA)}</td>}
              {tempBEnabled &&<td className={temperatureReadingClass(element.tempB)}>{formatTemperatureReading(element.tempB)}</td>}
              {showVoltage && (
                <td className={isLowVoltage(getVoltageValue(element)) ? "locationVoltsCell low-voltage" : "locationVoltsCell"}>
                  {renderVoltage(getVoltageValue(element))}
                </td>
              )}
              <td>{element.sat}</td>
              <td>{renderGps(element.gps)}</td>

              <td>{element.gsm}</td>
              {driverIDEnabled && <td>
                {element.driverID && 
                <ShowIButtonName 
                  ibuttonId={element.ibuttonId}
                  serialno={element.serialno} 
                  iButtonName={element.iButtonName}
                />}
              </td>}
              {driverLoginEnabled && <td>{element.driverName || element.DriverName || "-"}</td>}
              {doorSensorEnabled && <td>{renderDoorSensor(element.doorSensorStatus)}</td>}
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
  const handleDownloadExcel = () => {
    const csvData = locationData.map((element) => ({
      ID: element.id,
      Received: element.received,
      Created: element.created,
      Location: element.location,
      IO8: getIo8Value(element),
      Ignition: formatIgnition(element.ignition),
      LogID: element.logID,
      LogDescription: getLogDescription(element),
      Speed: formatOneDecimal(element.speed),
      Heading: formatHeading(element.heading),
      Trip: formatOneDecimal(element.trip),
      Odometer: formatOneDecimal(element.odometer),
      Duress: element.duress,
      Fridge: element.fridge == 1 ? "On" : "Off",
      ...(tempAEnabled ? { TempA: getValidTemperature(element.tempA) } : {}),
      ...(tempBEnabled ? { TempB: getValidTemperature(element.tempB) } : {}),
      ...(showVoltage ? { Volts: formatVolts(getVoltageValue(element)) } : {}),
      Sat: element.sat,
      GPS: element.gps,
      GSM: element.gsm,
      ...(driverIDEnabled ? { DriverID: element.driverID || "" } : {}),
      ...(driverLoginEnabled ? { Driver: element.driverName || element.DriverName || "" } : {}),
      ...(doorSensorEnabled ? { doorSensorStatus: element.doorSensorStatus } : {}),
    }));

    downloadCSVFile(csvData, "LocationDataReport", assetCustomerName, timeZone, assetId);
  }
  return (
    <ToggleBar
      caption={(
        <span className="locationDataTitle">
          Location Data
          <span className="locationDataRecordCount">{recordCount} records</span>
        </span>
      )}
      icon="fa fa-database"
      color="#A94442"
      content={data}
      download={handleDownloadExcel}
    />
  );
}

export default LocationTable;
